import { Component, NgZone, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Select } from '@ngxs/store';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { UserState } from '@state/user.state';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastService } from '@services/common/toast.service';
import { ConfirmationService } from 'primeng/api';
import { OrganizationState } from '@state/organization.state';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { QuestionnaireState } from '@state/questionnaire.state';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';

@Component({
    selector: 'app-settings-form-period',
    templateUrl: './settings-form-period.component.html',
    styleUrls: ['./settings-form-period.component.scss'],
    providers: [DialogService, ConfirmationService],
    standalone: false,
})
export class SettingsFormPeriodComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims | undefined>;
    @Select(OrganizationState.getOrganization) organization$!: Observable<OrganizationDto>;
    @Select(OrganizationState.getAvailableYears) availableYears$!: Observable<number[]>;
    @Select(OrganizationState.getQuestionnaires) questionnaires$!: Observable<QuestionnaireDto[]>;
    @Select(QuestionnaireState.getQuestionnaire) activeQuestionnaire$!: Observable<QuestionnaireDto>;
    @Select(OrganizationState.getOrganizationPricingPlan) pricingPlan$!: Observable<OrganizationPricingPlanDto>;
    public ref: DynamicDialogRef | undefined;

    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public organization: OrganizationDto;
    public organizationId: string = '';
    public availableYears: number[] = [];
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public existingPeriods: { period: number }[];
    public periodForm?: FormGroup;
    private ngUnsubscribe = new Subject<void>();
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public questionnaires: QuestionnaireDto[];
    public isEditMode: boolean = false;
    public selectedPeriod: number | undefined = undefined;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public activeQuestionnaire: QuestionnaireDto;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public pricingPlan: OrganizationPricingPlanDto;
    public isLoading: boolean = true;
    public canAddPeriod: boolean = false;
    public isOrgOwner: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        public dialogService: DialogService,
        public toastService: ToastService,
        private ngZone: NgZone,
    ) {
        this.periodForm = this.formBuilder.group({
            year: new FormControl('', Validators.required),
        });
    }

    ngOnInit(): void {
        combineLatest([
            this.organization$,
            this.availableYears$,
            this.questionnaires$,
            this.activeQuestionnaire$,
            this.pricingPlan$,
            this.userClaims$,
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: ([organization, availableYears, questionnaires, questionnaire, pricingplan, claims]) => {
                    if (
                        organization &&
                        organization.uid &&
                        availableYears &&
                        questionnaires &&
                        questionnaire &&
                        pricingplan &&
                        claims
                    ) {
                        this.organization = organization;
                        this.organizationId = organization.uid;
                        this.availableYears = availableYears;
                        this.questionnaires = questionnaires.filter((quest) => !quest.deleted);
                        this.activeQuestionnaire = questionnaire;
                        this.pricingPlan = pricingplan;
                        this.existingPeriods = this.questionnaires
                            .map((quest) => quest.refYear) // Extract the `refYear` from each questionnaire
                            .reduce<number[]>((acc, value) => {
                                // Build an array of unique years
                                if (!acc.includes(value)) {
                                    // Only add the year if it's not already included
                                    acc.push(value); // Add the unique year to the accumulator
                                }
                                return acc; // Return the updated accumulator
                            }, [])
                            .sort((a, b) => a - b) // Sort the years in ascending order
                            .map((year) => ({ period: year })); // Convert each year to an object with a `period` property

                        this.isOrgOwner = claims.tasks.some(
                            (task) => task.name === 'orguser' && task.organization_id === claims.organization?.uid,
                        );
                        this.canAddPeriod = this.pricingPlan.licenseDoc.multi_period === true && this.isOrgOwner;
                        this.isLoading = false;
                    }
                },
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    openEditPeriod(year: number | undefined = undefined): void {
        this.selectedPeriod = year;
        this.isEditMode = true;
    }

    onCloseEditPeriod(event?: { status: boolean; translateKey: string }) {
        this.isEditMode = false;
        if (event) {
            this.ngZone.run(() => {
                if (event.status) {
                    this.toastService.showSuccess({
                        details: event.translateKey,
                    });
                } else {
                    this.toastService.showError({
                        details: event.translateKey,
                    });
                }
            });
        }
    }
}
